// Resumen de «Accesorios» con los montos del servidor: panel lateral y barra inferior en tablet.
import type { ReactNode } from 'react';
import { X } from 'lucide-react';
import { Badge } from '@/kit';
import { cn } from '@/lib/cn';
import { variantLabel } from '@/shared/lib/format/capacity';
import { formatMXN } from '@/shared/lib/format/money';
import {
    SALE_PROMOTION_FALLBACK,
    SalePromotionPrice,
    SalePromotionTags,
} from '@/shared/patterns/SalePromotion';
import type { FinancingSplit, KitSwapCapChange, SaleSummary } from '../buildSale';
import { promotionRefsOf, type LinePromotionRef } from '../cartLines';
import { buildSaleNextStepName } from './BuildSaleHeading';
import { InlineActionButton } from './InlineActionButton';
import type { LineSlots } from './LineLabels';
import { ReservationClock } from './ReservationClock';

const mxn = (cents: number): string => formatMXN(cents / 100);

const LABEL = 'text-[11px] font-semibold uppercase tracking-[0.06em] text-muted-foreground';

type Gift = SaleSummary['gifts'][number];

/** Vencimiento que manda el reloj: la línea que vence primero (con su nombre si no es la ancla). */
export interface SummaryReservation {
    expiresAt: string;
    firstToExpireName?: string;
}

/** «El tope de extras a crédito pasó de $X a $Y»; `undefined` si el tope no cambió. */
export function kitCapChangeCopy(change: KitSwapCapChange): string | undefined {
    if (change.fromCents === change.toCents) return undefined;
    const cap = `El tope de extras a crédito pasó de ${mxn(change.fromCents)} a ${mxn(change.toCents)}`;
    return change.toCashCents > 0
        ? `${cap}; ${mxn(change.toCashCents)} de los extras pasa a contado.`
        : `${cap}.`;
}

/** Una etiqueta por promo: el nombre lo da Promociones (o «Promoción» sin pieza). */
function promotionNames(
    refs: readonly LinePromotionRef[],
    promotionLabel: LineSlots['promotionLabel'],
) {
    return refs.map((ref) =>
        promotionLabel ? (
            <span key={ref.promotionId}>
                {promotionLabel({
                    ...ref,
                    format: (n) => n,
                    fallback: SALE_PROMOTION_FALLBACK,
                    withAudience: true,
                })}
            </span>
        ) : (
            SALE_PROMOTION_FALLBACK
        ),
    );
}

/** Etiquetas de las promos del equipo y su código, cuando no llegan las piezas en lectura. */
function EquipmentPromotionTag({
    equipment,
    discounted,
    promotionLabel,
}: {
    equipment: NonNullable<SaleSummary['equipment']>;
    discounted: boolean;
    promotionLabel: LineSlots['promotionLabel'];
}) {
    const refs = discounted ? promotionRefsOf(equipment.line) : [];
    const names: ReactNode[] = promotionNames(refs, promotionLabel);
    if (equipment.discountCode) names.push(`Código ${equipment.discountCode}`);
    if (names.length === 0) return null;
    return (
        <span className="block" data-testid="equipment-discount">
            <SalePromotionTags names={names} />
        </span>
    );
}

/** ✕ de un renglón; sin handler deja el hueco para que los precios queden alineados. */
function RemoveButton({
    label,
    onClick,
    busy,
    loading,
}: {
    label: string;
    onClick: (() => void) | undefined;
    busy: boolean;
    loading: boolean;
}) {
    if (onClick === undefined) return <span aria-hidden className="w-5 flex-none" />;
    return (
        <InlineActionButton
            aria-label={label}
            title={label}
            disabled={busy}
            isLoading={loading}
            onClick={onClick}
            className="flex size-5 flex-none items-center justify-center rounded text-muted-foreground hover:bg-muted hover:text-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
        >
            {!loading && <X aria-hidden className="size-3.5" />}
        </InlineActionButton>
    );
}

function Price({ baseCents, netCents }: { baseCents: number | undefined; netCents: number }) {
    return baseCents !== undefined && baseCents > netCents ? (
        <SalePromotionPrice originalCents={baseCents} netCents={netCents} className="shrink-0" />
    ) : (
        <span className="shrink-0 tabular-nums">{mxn(netCents)}</span>
    );
}

/** «Financiable $X / $Y · De contado $Z» con el aviso del tope de extras debajo. */
function FinancingBox({
    financing,
    credit,
    hasExtras,
}: {
    financing: FinancingSplit;
    credit: NonNullable<SaleSummary['credit']>;
    hasExtras: boolean;
}) {
    const pct =
        financing.totalCents > 0
            ? Math.min(100, Math.round((financing.financedCents / financing.totalCents) * 100))
            : 0;
    const capReached = credit.financedCents >= credit.capCents && credit.cashCents > 0;
    return (
        <div
            data-testid="financing-split"
            className="flex flex-col gap-2 rounded-lg border border-border bg-muted/30 p-3 text-[12px]"
        >
            <div className="flex items-baseline justify-between gap-2">
                <span className={LABEL}>Financiable</span>
                <span className="tabular-nums">
                    <b>{mxn(financing.financedCents)}</b>
                    <span className="text-muted-foreground"> / {mxn(financing.totalCents)}</span>
                </span>
            </div>
            <div aria-hidden className="h-1.5 overflow-hidden rounded-full bg-muted">
                <span className="block h-full bg-success" style={{ width: `${String(pct)}%` }} />
            </div>
            <div className="flex items-baseline justify-between gap-2">
                <span className={LABEL}>De contado</span>
                <b className="tabular-nums">{mxn(financing.cashCents)}</b>
            </div>
            {hasExtras && (
                <div
                    data-testid="extras-cap"
                    className="flex justify-between gap-2 text-[11.5px] text-muted-foreground"
                >
                    <span>Accesorios a crédito</span>
                    <span className="tabular-nums">
                        {mxn(credit.financedCents)} / {mxn(credit.capCents)}
                    </span>
                </div>
            )}
            {capReached && (
                <p role="status" className="text-[11.5px] text-warning">
                    Llegaste al tope de extras a crédito; lo que pase va de contado.
                </p>
            )}
        </div>
    );
}

/** Motivo de bloqueo si lo hay; si no, a qué paso lleva «Continuar». */
function NextOrReason({ reason, referred }: { reason: ReactNode; referred: boolean }) {
    if (reason !== undefined && reason !== null && reason !== false) return <>{reason}</>;
    return (
        <p className="text-[12px] text-muted-foreground">
            Siguiente: {buildSaleNextStepName(referred)}
        </p>
    );
}

interface SharedProps {
    summary: SaleSummary;
    /** «Continuar»; no va en el resumen de solo lectura. */
    continueButton?: ReactNode;
    /** Motivo por el que «Continuar» no avanza (regalo, promos); reemplaza «Siguiente: …». */
    continueReason?: ReactNode;
    /** Total sin las promos solo SureKeep; se muestra debajo solo si difiere. */
    cashTotalCents?: number | undefined;
    /** Reparto del servidor (`financingSplitOf`); la barra sale solo si además hay `summary.credit`. */
    financing?: FinancingSplit | undefined;
    /** `false` sin nivel SureKeep: no hay barra de financiable. */
    showFinancing?: boolean;
    /** Venta derivada: «Continuar» lleva a Cobro. */
    referred?: boolean;
    /** Línea que vence primero; sin ella manda el vencimiento del equipo. */
    reservation?: SummaryReservation | undefined;
    /** «Vuelve a elegir el teléfono» cuando el apartado venció. */
    onReselect?: (() => void) | undefined;
}

/** Reparto a pintar: solo con SureKeep (`summary.credit` lo quita `summaryForSession`) y datos del servidor. */
function financingOf({
    financing,
    showFinancing = true,
    summary,
}: Pick<SharedProps, 'financing' | 'showFinancing' | 'summary'>):
    | { split: FinancingSplit; credit: NonNullable<SaleSummary['credit']> }
    | undefined {
    if (!showFinancing || financing === undefined || summary.credit === undefined) return undefined;
    return { split: financing, credit: summary.credit };
}

function Clock({
    summary,
    reservation,
    onReselect,
}: Pick<SharedProps, 'summary' | 'reservation' | 'onReselect'>) {
    const line = summary.equipment?.line;
    if (line === undefined) return null;
    return (
        <span data-testid="reservation-clock" className="text-[11px] text-muted-foreground">
            <ReservationClock
                expiresAt={reservation?.expiresAt ?? line.expiresAt}
                firstToExpireName={reservation?.firstToExpireName}
                phoneExpiresAt={line.expiresAt}
                onReselect={onReselect}
            />
        </span>
    );
}

/** Panel lateral; debajo de lg va al final de la columna y la barra lleva «Continuar». */
export function BuildSaleSummaryPanel({
    summary,
    continueButton,
    continueReason,
    cashTotalCents,
    financing,
    showFinancing,
    referred = false,
    reservation,
    onReselect,
    afterSale,
    giftName,
    promotionLabel,
    variants,
    kitPieces = [],
    kitCapChange,
    phonePromotions,
    discountCode,
    kitPromotions,
    onRemoveKit,
    onRemoveGift,
    onRemoveExtra,
    busy = false,
    removingKey,
    readOnly = false,
}: SharedProps & {
    /** Resumen de lectura (Forma de pago): sin «Continuar», «Siguiente» ni reloj. */
    readOnly?: boolean;
    /** «Después en la venta»: pieza de Promociones; trae su encabezado y no pinta nada si está vacía. */
    afterSale?: ReactNode;
    /** Nombre del regalo según Promociones; sin pieza queda el del catálogo. */
    giftName?: (gift: Gift) => ReactNode;
    /** Nombre de la promo del equipo según Promociones; sin pieza, «Promoción». */
    promotionLabel?: LineSlots['promotionLabel'];
    /** Variante por SKU (`lineVariantsBySku`) para regalos y extras. */
    variants?: ReadonlyMap<string, string>;
    /** Piezas del kit (`kitPieceNamesOf`), debajo del equipo cuando es kit. */
    kitPieces?: readonly string[];
    /** Cambio de tope al pasar a kit (`kitSwapCapChange`); sale una línea corta si cambió. */
    kitCapChange?: KitSwapCapChange | undefined;
    /** Promos del teléfono en lectura; con este hueco o `discountCode` no van las etiquetas propias. */
    phonePromotions?: ReactNode;
    /** Cupón en lectura. */
    discountCode?: ReactNode;
    /** «Promos del kit (N) · Ver». */
    kitPromotions?: ReactNode;
    /** ✕ «Quitar kit»: regresa al teléfono. */
    onRemoveKit?: (() => void) | undefined;
    /** ✕ del regalo: quita la promo del regalo. */
    onRemoveGift?: ((gift: Gift) => void) | undefined;
    /** ✕ del extra: quita todas las piezas de ese SKU. */
    onRemoveExtra?: ((sku: string) => void) | undefined;
    /** Bloquea las ✕ mientras algo se escribe. */
    busy?: boolean;
    /** Renglón que se está quitando: `sku` del extra, `key` del regalo o `'kit'`. */
    removingKey?: string | undefined;
}) {
    const { equipment, gifts, extras, totalCents } = summary;
    const discount = equipment ? equipment.baseCents - equipment.netCents : 0;
    const hasReadSlots = phonePromotions !== undefined || discountCode !== undefined;
    const capCopy = kitCapChange && equipment?.isKit ? kitCapChangeCopy(kitCapChange) : undefined;
    const variantOf = (sku: string): string | undefined => variants?.get(sku);
    const shown = financingOf({ financing, showFinancing, summary });
    return (
        <aside
            aria-label="Resumen de la venta"
            className={cn(
                'flex flex-col gap-2.5 rounded-lg border border-border bg-card p-4 text-[13px]',
                !readOnly && 'lg:sticky lg:top-3',
            )}
        >
            <h2 className="text-[14px] font-semibold">Resumen</h2>
            {equipment && (
                <div data-testid="summary-equipment" className="flex flex-col gap-1.5">
                    <div className="flex items-start gap-2">
                        <span className="flex min-w-0 flex-1 flex-col">
                            <span className="flex min-w-0 items-center gap-1.5">
                                <b className="min-w-0 truncate" title={equipment.name}>
                                    {equipment.name}
                                </b>
                                {equipment.isKit && (
                                    <Badge variant="outline" className="flex-none text-[10px]">
                                        KIT
                                    </Badge>
                                )}
                            </span>
                            {!equipment.isKit && variantLabel(equipment.line.product ?? {}) && (
                                <span className="text-[12px] text-muted-foreground">
                                    {variantLabel(equipment.line.product ?? {})}
                                </span>
                            )}
                            {!readOnly && (
                                <Clock
                                    summary={summary}
                                    reservation={reservation}
                                    onReselect={onReselect}
                                />
                            )}
                            {!hasReadSlots && (
                                <EquipmentPromotionTag
                                    equipment={equipment}
                                    discounted={discount > 0}
                                    promotionLabel={promotionLabel}
                                />
                            )}
                        </span>
                        <Price baseCents={equipment.baseCents} netCents={equipment.netCents} />
                        <RemoveButton
                            label="Quitar kit"
                            onClick={equipment.isKit ? onRemoveKit : undefined}
                            busy={busy}
                            loading={removingKey === 'kit'}
                        />
                    </div>
                    {equipment.isKit && kitPieces.length > 0 && (
                        <ul
                            aria-label="Piezas del kit"
                            className="ml-1 flex flex-col gap-0.5 border-l-2 border-border pl-3 text-[12px] text-muted-foreground"
                        >
                            {kitPieces.map((piece) => (
                                <li key={piece} className="truncate" title={piece}>
                                    {piece}
                                </li>
                            ))}
                        </ul>
                    )}
                    {capCopy !== undefined && (
                        <p role="status" className="text-[12px] text-warning">
                            {capCopy}
                        </p>
                    )}
                    {phonePromotions}
                    {discountCode}
                    {kitPromotions}
                </div>
            )}
            {gifts.map((g) => {
                const variant = variantOf(g.sku);
                return (
                    <div key={g.key} data-testid="summary-gift" className="flex items-start gap-2">
                        <span className="flex min-w-0 flex-1 flex-col">
                            <span className="min-w-0 truncate">
                                {giftName ? giftName(g) : g.name}
                            </span>
                            {variant && (
                                <span className="text-[12px] text-muted-foreground">{variant}</span>
                            )}
                        </span>
                        <span className="flex shrink-0 gap-1.5 tabular-nums">
                            <s className="text-muted-foreground">{mxn(g.valueCents)}</s>
                            <span className="text-info">{mxn(0)}</span>
                        </span>
                        <RemoveButton
                            label={`Quitar regalo ${g.name}`}
                            onClick={
                                onRemoveGift &&
                                (() => {
                                    onRemoveGift(g);
                                })
                            }
                            busy={busy}
                            loading={removingKey === g.key}
                        />
                    </div>
                );
            })}
            {extras.map((e) => {
                const variant = variantOf(e.sku);
                return (
                    <div key={e.sku} data-testid="summary-extra" className="flex items-start gap-2">
                        <span className="flex min-w-0 flex-1 flex-col">
                            <span className="flex min-w-0 items-baseline gap-1.5">
                                <span className="min-w-0 truncate" title={e.name}>
                                    {e.name}
                                </span>
                                {e.qty > 1 && (
                                    <span className="flex-none text-[12px] text-muted-foreground tabular-nums">
                                        ×{e.qty}
                                    </span>
                                )}
                            </span>
                            {variant && (
                                <span className="text-[12px] text-muted-foreground">{variant}</span>
                            )}
                        </span>
                        <Price baseCents={e.baseCents} netCents={e.totalCents} />
                        <RemoveButton
                            label={`Quitar ${e.name}`}
                            onClick={
                                onRemoveExtra &&
                                (() => {
                                    onRemoveExtra(e.sku);
                                })
                            }
                            busy={busy}
                            loading={removingKey === e.sku}
                        />
                    </div>
                );
            })}
            <div className="border-t border-border" />
            <div className="flex items-baseline justify-between gap-3">
                <span className="font-semibold">Total</span>
                <span className="flex flex-col items-end">
                    <b className="text-[18px] tabular-nums">{mxn(totalCents)}</b>
                    {cashTotalCents !== undefined && cashTotalCents !== totalCents && (
                        <span
                            data-testid="cash-total"
                            className="text-[11.5px] text-muted-foreground tabular-nums"
                        >
                            De contado {mxn(cashTotalCents)}
                        </span>
                    )}
                </span>
            </div>
            {shown && (
                <FinancingBox
                    financing={shown.split}
                    credit={shown.credit}
                    hasExtras={extras.length > 0}
                />
            )}
            {!readOnly && (
                <div className="hidden flex-col gap-1.5 lg:flex">
                    {continueButton}
                    <NextOrReason reason={continueReason} referred={referred} />
                </div>
            )}
            {afterSale}
        </aside>
    );
}

/** Barra inferior fija en tablet: total, reloj, financiable y «Continuar». */
export function BuildSaleSummaryBar({
    summary,
    continueButton,
    continueReason,
    cashTotalCents,
    financing,
    showFinancing,
    referred = false,
    reservation,
    onReselect,
}: SharedProps) {
    const split = financingOf({ financing, showFinancing, summary })?.split;
    return (
        <div className="fixed inset-x-0 bottom-0 z-10 flex items-center gap-3 border-t border-border bg-card px-4 py-2.5 shadow-[0_-4px_14px_rgba(0,0,0,0.06)] lg:hidden">
            <div className="flex min-w-0 flex-1 flex-col gap-0.5">
                <div className="flex justify-between gap-2 text-[12px]">
                    <span>Total</span>
                    <span className="flex flex-col items-end">
                        <b className="text-[16px] tabular-nums">{mxn(summary.totalCents)}</b>
                        {cashTotalCents !== undefined && cashTotalCents !== summary.totalCents && (
                            <span
                                data-testid="cash-total-bar"
                                className="text-[11px] text-muted-foreground tabular-nums"
                            >
                                De contado {mxn(cashTotalCents)}
                            </span>
                        )}
                    </span>
                </div>
                <div className="flex flex-wrap items-baseline justify-between gap-x-3">
                    <Clock summary={summary} reservation={reservation} onReselect={onReselect} />
                    {split && (
                        <span
                            data-testid="financing-split-bar"
                            className="text-[11px] text-muted-foreground tabular-nums"
                        >
                            Financiable {mxn(split.financedCents)} / {mxn(split.totalCents)} · De
                            contado {mxn(split.cashCents)}
                        </span>
                    )}
                </div>
            </div>
            <div className="flex max-w-[55%] flex-none flex-col items-end gap-1 text-right">
                {continueButton}
                <NextOrReason reason={continueReason} referred={referred} />
            </div>
        </div>
    );
}
